import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { courseAPI, kioskAPI, teacherAPI, teksAPI } from "../services/api";
import "./ObjectiveLibraryPage.css";

const CSV_TEMPLATE_HEADERS = [
  "teks_code",
  "learning_objective",
  "i_can_statements",
  "unit_label",
  "lesson_label",
  "objective_date",
  "week_start_date",
  "week_label",
  "teks_full_text",
  "notes",
];
const MAX_CSV_IMPORT_CHARS = 500_000;
const BULK_CREATE_COURSE_OPTION = "__bulk_create_course__";

const emptyCourseDraft = (canonicalCourseId = "") => ({
  title: "",
  subject: "",
  gradeLevel: "",
  canonicalCourseId: canonicalCourseId || "",
  isArchived: false,
});

const WEEKDAY_OPTIONS = [
  { key: "monday", label: "Monday", offset: 0 },
  { key: "tuesday", label: "Tuesday", offset: 1 },
  { key: "wednesday", label: "Wednesday", offset: 2 },
  { key: "thursday", label: "Thursday", offset: 3 },
  { key: "friday", label: "Friday", offset: 4 },
];

const emptyWeekRow = () => ({
  dayKey: "monday",
  teksCodes: "",
  learningObjective: "",
  iCanText: "",
  notes: "",
});

const emptyWeekDraft = (objectiveCourseId = "", sourceCourseId = "") => ({
  objectiveCourseId: objectiveCourseId || "",
  sourceCourseId: sourceCourseId || "",
  weekStartDate: "",
  weekLabel: "",
  unitLabel: "",
  lessonLabel: "",
  mode: "whole_week",
  rows: [emptyWeekRow()],
});

const emptyEntryDraft = (objectiveCourseId = "") => ({
  id: null,
  objectiveCourseId: objectiveCourseId || "",
  unitLabel: "",
  lessonLabel: "",
  objectiveDate: "",
  weekStartDate: "",
  weekLabel: "",
  teksCode: "",
  teksFullText: "",
  learningObjective: "",
  iCanText: "",
  notes: "",
});

const toDateKey = (value) => {
  if (!value) return "";
  const raw = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  if (raw.includes("T")) {
    const maybe = raw.split("T")[0];
    if (/^\d{4}-\d{2}-\d{2}$/.test(maybe)) return maybe;
  }
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toISOString().split("T")[0];
};

const toMondayDateKey = (value) => {
  const dateKey = toDateKey(value);
  if (!dateKey) return "";
  const [yearRaw, monthRaw, dayRaw] = dateKey.split("-");
  const year = Number(yearRaw);
  const month = Number(monthRaw);
  const day = Number(dayRaw);
  const date = new Date(Date.UTC(year, month - 1, day));
  const isoDay = ((date.getUTCDay() + 6) % 7) + 1;
  date.setUTCDate(date.getUTCDate() - (isoDay - 1));
  return date.toISOString().split("T")[0];
};

const addDaysToDateKey = (dateKey, daysToAdd) => {
  const normalized = toDateKey(dateKey);
  if (!normalized) return "";
  const [yearRaw, monthRaw, dayRaw] = normalized.split("-");
  const year = Number(yearRaw);
  const month = Number(monthRaw);
  const day = Number(dayRaw);
  const date = new Date(Date.UTC(year, month - 1, day));
  date.setUTCDate(date.getUTCDate() + Number(daysToAdd || 0));
  return date.toISOString().split("T")[0];
};

const toWeekDayKey = (dateValue) => {
  const dateKey = toDateKey(dateValue);
  if (!dateKey) return "monday";
  const [yearRaw, monthRaw, dayRaw] = dateKey.split("-");
  const year = Number(yearRaw);
  const month = Number(monthRaw);
  const day = Number(dayRaw);
  const date = new Date(Date.UTC(year, month - 1, day));
  const utcDay = date.getUTCDay();
  if (utcDay === 1) return "monday";
  if (utcDay === 2) return "tuesday";
  if (utcDay === 3) return "wednesday";
  if (utcDay === 4) return "thursday";
  if (utcDay === 5) return "friday";
  return "monday";
};

const toTeksCodes = (value) =>
  String(value || "")
    .split(/[\n,;|]+/)
    .map((item) => item.trim())
    .filter(Boolean);

const normalizeTeksToken = (value) => String(value || "").trim().toLowerCase();

const matchesAllowedTeksToken = (rawToken, allowedTokens) => {
  const normalizedToken = normalizeTeksToken(rawToken);
  if (!normalizedToken) return false;
  if (allowedTokens.has(normalizedToken)) return true;
  if (!normalizedToken.includes("/")) return false;

  const slashParts = normalizedToken
    .split("/")
    .map((part) => part.trim())
    .filter(Boolean);
  if (slashParts.length < 2) return false;
  return slashParts.every((part) => allowedTokens.has(part));
};

const toICanArray = (value) =>
  String(value || "")
    .split("\n")
    .map((line) => line.replace(/^[\s\u2022*-]+/, "").trim())
    .filter(Boolean);

const toICanText = (value) => {
  if (!Array.isArray(value)) return "";
  return value
    .map((line) => String(line || "").trim())
    .filter(Boolean)
    .join("\n");
};

const getApiErrorMessage = (error, fallback) => {
  const apiError = String(error?.response?.data?.error || "").trim();
  return apiError || fallback;
};

const readFileAsText = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Unable to read CSV file."));
    reader.readAsText(file);
  });

const buildAiCsvPromptFromScratch = ({ selectedCourseTitle }) => {
  const courseLabel = selectedCourseTitle
    ? `Target course in app UI (not in CSV): ${selectedCourseTitle}.`
    : "Target course in app UI (not in CSV): selected in Objective Library.";

  return [
    "You are helping me prepare CSV rows for an Objective Library bulk upload.",
    courseLabel,
    "Important: Do NOT include course or course_id columns.",
    `Output ONLY CSV that will pass validation, using this exact header row and column order: ${CSV_TEMPLATE_HEADERS.join(",")}`,
    "Required columns: teks_code, learning_objective.",
    "Optional columns: i_can_statements, unit_label, lesson_label, objective_date, week_start_date, week_label, teks_full_text, notes.",
    "Formatting rules:",
    "- objective_date must be YYYY-MM-DD.",
    "- week_start_date must be YYYY-MM-DD and a Monday.",
    "- Include a weekly marker for every row: provide week_label and week_start_date.",
    "- If week information is not provided in source notes, infer a reasonable week_label and Monday week_start_date.",
    "- i_can_statements should be in one cell separated by |",
    "- Escape commas and quotes correctly for CSV.",
    "Return ONLY raw CSV text (header + rows). No markdown, no explanation.",
    "Generate 10 realistic rows now that should pass validation.",
  ].join("\n");
};

const buildAiCsvPromptNormalizeText = () =>
  [
    "Convert my objective notes into CSV rows for Objective Library upload.",
    "Use this exact header and order:",
    CSV_TEMPLATE_HEADERS.join(","),
    "Rules:",
    "- Do NOT add a course/course_id column.",
    "- Keep teks_code and learning_objective populated for every row.",
    "- Use YYYY-MM-DD for objective_date.",
    "- If provided, week_start_date must be a Monday in YYYY-MM-DD.",
    "- Include weekly markers for every row: week_label and week_start_date.",
    "- If weekly marker is missing from my notes, infer a week_label and Monday week_start_date.",
    "- Put multiple i_can_statements in one cell separated by |",
    "- Output must pass CSV validation in the app.",
    "- Return ONLY CSV (header + rows), no markdown, no explanation.",
    "My notes:",
    "[PASTE NOTES HERE]",
  ].join("\n");

const mapCourseToDraft = (course) => ({
  title: String(course?.title || ""),
  subject: String(course?.subject || ""),
  gradeLevel:
    course?.grade_level === null || typeof course?.grade_level === "undefined"
      ? ""
      : String(course.grade_level),
  canonicalCourseId: String(course?.canonical_course_id || ""),
  isArchived: Boolean(course?.is_archived),
});

const ObjectiveLibraryPage = ({ currentCourse }) => {
  const [canonicalCourses, setCanonicalCourses] = useState([]);
  const [objectiveCourses, setObjectiveCourses] = useState([]);
  const [selectedCourseId, setSelectedCourseId] = useState("");
  const [entries, setEntries] = useState([]);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [coursesLoading, setCoursesLoading] = useState(false);
  const [entriesLoading, setEntriesLoading] = useState(false);
  const [coursesError, setCoursesError] = useState("");
  const [entriesError, setEntriesError] = useState("");
  const [statusMessage, setStatusMessage] = useState("");
  const [isCourseSettingsModalOpen, setIsCourseSettingsModalOpen] = useState(false);
  const [isEntryModalOpen, setIsEntryModalOpen] = useState(false);
  const [isWeekModalOpen, setIsWeekModalOpen] = useState(false);
  const [showCreateCourseForm, setShowCreateCourseForm] = useState(false);
  const [newCourseDraft, setNewCourseDraft] = useState(() =>
    emptyCourseDraft(currentCourse || ""),
  );
  const [selectedCourseDraft, setSelectedCourseDraft] = useState(emptyCourseDraft());
  const [entryDraft, setEntryDraft] = useState(emptyEntryDraft());
  const [filterDraft, setFilterDraft] = useState({
    unitLabel: "",
    lessonLabel: "",
    weekStartDate: "",
    weekLabel: "",
    dateFrom: "",
    dateTo: "",
  });
  const [appliedFilters, setAppliedFilters] = useState({
    unitLabel: "",
    lessonLabel: "",
    weekStartDate: "",
    weekLabel: "",
    dateFrom: "",
    dateTo: "",
  });
  const [filterOptionEntries, setFilterOptionEntries] = useState([]);
  const [bulkCsvText, setBulkCsvText] = useState("");
  const [bulkCsvFileName, setBulkCsvFileName] = useState("");
  const [bulkError, setBulkError] = useState("");
  const [bulkValidation, setBulkValidation] = useState(null);
  const [bulkValidating, setBulkValidating] = useState(false);
  const [bulkImporting, setBulkImporting] = useState(false);
  const [showBulkTools, setShowBulkTools] = useState(false);
  const [showBulkHelpers, setShowBulkHelpers] = useState(false);
  const [aiPromptStatus, setAiPromptStatus] = useState("");
  const [weekDraft, setWeekDraft] = useState(emptyWeekDraft());
  const [weekSaving, setWeekSaving] = useState(false);
  const [weekLoadingDefaults, setWeekLoadingDefaults] = useState(false);
  const [weekStatusMessage, setWeekStatusMessage] = useState("");
  const [weekCourseTitle, setWeekCourseTitle] = useState("");
  const [weekCourseSuggestedTitle, setWeekCourseSuggestedTitle] = useState("");
  const [weekCourseCreating, setWeekCourseCreating] = useState(false);
  const [weekCourseTeksOptions, setWeekCourseTeksOptions] = useState([]);
  const [weekCourseTeksLoading, setWeekCourseTeksLoading] = useState(false);
  const [weekCourseTeksError, setWeekCourseTeksError] = useState("");
  const [activeWeekTeksRowKey, setActiveWeekTeksRowKey] = useState("");
  const [weekTeksSearchInput, setWeekTeksSearchInput] = useState("");
  const [weekTeksSearchDebounced, setWeekTeksSearchDebounced] = useState("");
  const activeWeekTeksRowKeyRef = useRef("");

  const canonicalCourseMap = useMemo(() => {
    const map = {};
    (canonicalCourses || []).forEach((course) => {
      map[String(course.id)] = course;
    });
    return map;
  }, [canonicalCourses]);

  const selectedCourse = useMemo(
    () => objectiveCourses.find((course) => String(course.id) === String(selectedCourseId)) || null,
    [objectiveCourses, selectedCourseId],
  );
  const selectedWeekCanonicalCourseId = useMemo(
    () => String(weekDraft.sourceCourseId || "").trim(),
    [weekDraft.sourceCourseId],
  );
  const selectedWeekSourceCourse = useMemo(
    () => canonicalCourseMap[selectedWeekCanonicalCourseId] || null,
    [canonicalCourseMap, selectedWeekCanonicalCourseId],
  );
  const suggestedWeekCourseTitle = useMemo(() => {
    const sourceName = String(selectedWeekSourceCourse?.name || "").trim();
    if (!sourceName) return "";
    return `My ${sourceName}`;
  }, [selectedWeekSourceCourse?.name]);
  const unitOptions = useMemo(() => {
    const values = new Set();
    filterOptionEntries.forEach((entry) => {
      const value = String(entry?.unit_label || "").trim();
      if (value) values.add(value);
    });
    if (filterDraft.unitLabel) values.add(filterDraft.unitLabel);
    return Array.from(values).sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" }),
    );
  }, [filterOptionEntries, filterDraft.unitLabel]);
  const lessonOptions = useMemo(() => {
    const values = new Set();
    const rows =
      filterDraft.unitLabel
        ? filterOptionEntries.filter(
            (entry) => String(entry?.unit_label || "").trim() === filterDraft.unitLabel,
          )
        : filterOptionEntries;
    rows.forEach((entry) => {
      const value = String(entry?.lesson_label || "").trim();
      if (value) values.add(value);
    });
    if (filterDraft.lessonLabel) values.add(filterDraft.lessonLabel);
    return Array.from(values).sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" }),
    );
  }, [filterOptionEntries, filterDraft.lessonLabel, filterDraft.unitLabel]);
  const weekOptions = useMemo(() => {
    const values = new Map();
    filterOptionEntries.forEach((entry) => {
      const weekStartDate = toDateKey(entry?.week_start_date);
      if (!weekStartDate) return;
      const weekLabel = String(entry?.week_label || "").trim();
      if (!values.has(weekStartDate)) {
        values.set(weekStartDate, weekLabel);
      }
    });
    if (filterDraft.weekStartDate && !values.has(filterDraft.weekStartDate)) {
      values.set(filterDraft.weekStartDate, "");
    }
    return Array.from(values.entries())
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([weekStartDate, weekLabel]) => ({
        value: weekStartDate,
        label: weekLabel ? `${weekLabel} (${weekStartDate})` : `Week of ${weekStartDate}`,
      }));
  }, [filterOptionEntries, filterDraft.weekStartDate]);
  const weekLabelOptions = useMemo(() => {
    const values = new Set();
    const rows =
      filterDraft.weekStartDate
        ? filterOptionEntries.filter(
            (entry) => toDateKey(entry?.week_start_date) === filterDraft.weekStartDate,
          )
        : filterOptionEntries;
    rows.forEach((entry) => {
      const value = String(entry?.week_label || "").trim();
      if (value) values.add(value);
    });
    if (filterDraft.weekLabel) values.add(filterDraft.weekLabel);
    return Array.from(values).sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" }),
    );
  }, [filterOptionEntries, filterDraft.weekLabel, filterDraft.weekStartDate]);
  const aiPromptFromScratch = useMemo(
    () =>
      buildAiCsvPromptFromScratch({
        selectedCourseTitle: String(selectedCourse?.title || "").trim(),
      }),
    [selectedCourse?.title],
  );
  const aiPromptNormalizeText = useMemo(() => buildAiCsvPromptNormalizeText(), []);

  const loadObjectiveCourses = useCallback(async () => {
    setCoursesLoading(true);
    setCoursesError("");
    try {
      const response = await teacherAPI.listObjectiveCourses(includeArchived);
      const rows = Array.isArray(response.data) ? response.data : [];
      setObjectiveCourses(rows);
      setSelectedCourseId((prev) => {
        if (prev && rows.some((course) => String(course.id) === String(prev))) return prev;
        return rows[0]?.id || "";
      });
    } catch (error) {
      console.error("Error loading teacher objective courses:", error);
      setObjectiveCourses([]);
      setSelectedCourseId("");
      setCoursesError(
        getApiErrorMessage(
          error,
          "Could not load your objective courses. Sign in and verify the migration is applied.",
        ),
      );
    } finally {
      setCoursesLoading(false);
    }
  }, [includeArchived]);

  const loadObjectiveEntries = useCallback(
    async (objectiveCourseId, filters = appliedFilters) => {
      if (!objectiveCourseId) {
        setEntries([]);
        return;
      }

      setEntriesLoading(true);
      setEntriesError("");
      try {
        const response = await teacherAPI.listObjectiveEntries({
          objectiveCourseId,
          unitLabel: filters.unitLabel || undefined,
          lessonLabel: filters.lessonLabel || undefined,
          weekStartDate: filters.weekStartDate || undefined,
          weekLabel: filters.weekLabel || undefined,
          dateFrom: filters.dateFrom || undefined,
          dateTo: filters.dateTo || undefined,
        });
        setEntries(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error("Error loading objective entries:", error);
        setEntries([]);
        setEntriesError(
          getApiErrorMessage(
            error,
            "Could not load objective entries for this course.",
          ),
        );
      } finally {
        setEntriesLoading(false);
      }
    },
    [appliedFilters],
  );

  const loadFilterOptions = useCallback(async (objectiveCourseId) => {
    if (!objectiveCourseId) {
      setFilterOptionEntries([]);
      return;
    }
    try {
      const response = await teacherAPI.listObjectiveEntries({ objectiveCourseId, limit: 500 });
      setFilterOptionEntries(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error loading objective entry filter options:", error);
      setFilterOptionEntries([]);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadCanonicalCourses = async () => {
      try {
        const response = await courseAPI.getAll();
        if (cancelled) return;
        const rows = Array.isArray(response.data) ? response.data : [];
        setCanonicalCourses(rows);
      } catch (error) {
        if (cancelled) return;
        console.error("Error loading canonical courses for objective library:", error);
        setCanonicalCourses([]);
      }
    };
    loadCanonicalCourses();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    loadObjectiveCourses();
  }, [loadObjectiveCourses]);

  useEffect(() => {
    if (!selectedCourse) {
      setSelectedCourseDraft(emptyCourseDraft(currentCourse || ""));
      setEntryDraft(emptyEntryDraft());
      return;
    }
    setSelectedCourseDraft(mapCourseToDraft(selectedCourse));
    setEntryDraft(emptyEntryDraft(selectedCourse.id));
  }, [currentCourse, selectedCourse]);

  useEffect(() => {
    loadObjectiveEntries(selectedCourseId, appliedFilters);
  }, [selectedCourseId, appliedFilters, loadObjectiveEntries]);

  useEffect(() => {
    loadFilterOptions(selectedCourseId);
  }, [selectedCourseId, loadFilterOptions]);

  useEffect(() => {
    setNewCourseDraft((prev) => {
      if (prev.canonicalCourseId || !currentCourse) return prev;
      return { ...prev, canonicalCourseId: currentCourse };
    });
  }, [currentCourse]);

  useEffect(() => {
    setBulkValidation(null);
    setBulkError("");
  }, [selectedCourseId]);

  useEffect(() => {
    if (selectedCourse) return;
    setIsCourseSettingsModalOpen(false);
    setIsEntryModalOpen(false);
  }, [selectedCourse]);

  useEffect(() => {
    if (!aiPromptStatus) return undefined;
    const timer = window.setTimeout(() => setAiPromptStatus(""), 2500);
    return () => window.clearTimeout(timer);
  }, [aiPromptStatus]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setWeekTeksSearchDebounced(weekTeksSearchInput);
    }, 250);
    return () => window.clearTimeout(handle);
  }, [weekTeksSearchInput]);

  useEffect(() => {
    activeWeekTeksRowKeyRef.current = activeWeekTeksRowKey;
  }, [activeWeekTeksRowKey]);

  useEffect(() => {
    if (!isWeekModalOpen) {
      setActiveWeekTeksRowKey("");
      setWeekTeksSearchInput("");
      setWeekTeksSearchDebounced("");
      return;
    }

    if (!selectedWeekCanonicalCourseId) {
      setWeekCourseTeksOptions([]);
      setWeekCourseTeksLoading(false);
      setWeekCourseTeksError("Select a source district course to use TEKS suggestions.");
      return;
    }

    let cancelled = false;
    const loadWeekCourseTeks = async () => {
      setWeekCourseTeksLoading(true);
      setWeekCourseTeksError("");
      try {
        const response = await teksAPI.getAll(selectedWeekCanonicalCourseId);
        if (cancelled) return;
        const rows = Array.isArray(response.data) ? response.data : [];
        const options = rows
          .map((row) => ({
            code: String(row?.teks_code || "").trim(),
            description: String(row?.description || row?.teks_description || "").trim(),
          }))
          .filter((row) => row.code)
          .sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }));
        setWeekCourseTeksOptions(options);
      } catch (error) {
        if (cancelled) return;
        console.error("Error loading TEKS for custom objective modal:", error);
        setWeekCourseTeksOptions([]);
        setWeekCourseTeksError("Could not load TEKS for this source district course.");
      } finally {
        if (!cancelled) setWeekCourseTeksLoading(false);
      }
    };

    loadWeekCourseTeks();
    return () => {
      cancelled = true;
    };
  }, [isWeekModalOpen, selectedWeekCanonicalCourseId]);

  useEffect(() => {
    if (!isWeekModalOpen) return;
    if (weekDraft.objectiveCourseId) return;
    if (!suggestedWeekCourseTitle) return;

    const currentTitle = String(weekCourseTitle || "").trim();
    const previousSuggestion = String(weekCourseSuggestedTitle || "").trim();
    if (!currentTitle || (previousSuggestion && currentTitle === previousSuggestion)) {
      setWeekCourseTitle(suggestedWeekCourseTitle);
      setWeekCourseSuggestedTitle(suggestedWeekCourseTitle);
    }
  }, [
    isWeekModalOpen,
    suggestedWeekCourseTitle,
    weekCourseSuggestedTitle,
    weekCourseTitle,
    weekDraft.objectiveCourseId,
  ]);

  const filteredWeekCourseTeksOptions = useMemo(() => {
    const query = String(weekTeksSearchDebounced || "").trim().toLowerCase();
    const options = weekCourseTeksOptions.filter((option) => {
      if (!query) return true;
      return (
        String(option.code || "").toLowerCase().includes(query)
        || String(option.description || "").toLowerCase().includes(query)
      );
    });
    return options.slice(0, 12);
  }, [weekCourseTeksOptions, weekTeksSearchDebounced]);

  const resetEntryDraft = useCallback(
    (objectiveCourseId = selectedCourseId) => {
      setEntryDraft(emptyEntryDraft(objectiveCourseId || ""));
    },
    [selectedCourseId],
  );

  const resetWeekDraft = useCallback(
    (
      objectiveCourseId = selectedCourseId,
      sourceCourseId = currentCourse,
    ) => {
      const draft = emptyWeekDraft(objectiveCourseId || "", sourceCourseId || "");
      draft.weekStartDate = toMondayDateKey(new Date());
      setWeekDraft(draft);
      setWeekStatusMessage("");
      setWeekCourseTitle("");
      setWeekCourseSuggestedTitle("");
    },
    [currentCourse, selectedCourseId],
  );

  const handleCreateCourse = async (event) => {
    event.preventDefault();
    const title = String(newCourseDraft.title || "").trim();
    if (!title) {
      setStatusMessage("Course title is required.");
      return;
    }

    try {
      const response = await teacherAPI.createObjectiveCourse({
        title,
        subject: newCourseDraft.subject || null,
        gradeLevel:
          String(newCourseDraft.gradeLevel || "").trim() === ""
            ? null
            : Number(newCourseDraft.gradeLevel),
        canonicalCourseId: newCourseDraft.canonicalCourseId || null,
      });
      const created = response.data;
      setStatusMessage("Objective course created.");
      setNewCourseDraft(emptyCourseDraft(newCourseDraft.canonicalCourseId || currentCourse || ""));
      setShowCreateCourseForm(false);
      await loadObjectiveCourses();
      setSelectedCourseId(String(created?.id || ""));
    } catch (error) {
      console.error("Error creating objective course:", error);
      setStatusMessage(
        getApiErrorMessage(error, "Could not create objective course."),
      );
    }
  };

  const handleSaveCourseDetails = async () => {
    if (!selectedCourseId) return;
    const title = String(selectedCourseDraft.title || "").trim();
    if (!title) {
      setStatusMessage("Selected course title cannot be blank.");
      return;
    }

    try {
      await teacherAPI.updateObjectiveCourse(selectedCourseId, {
        title,
        subject: selectedCourseDraft.subject || null,
        gradeLevel:
          String(selectedCourseDraft.gradeLevel || "").trim() === ""
            ? null
            : Number(selectedCourseDraft.gradeLevel),
        canonicalCourseId: selectedCourseDraft.canonicalCourseId || null,
        isArchived: Boolean(selectedCourseDraft.isArchived),
      });
      setStatusMessage("Objective course updated.");
      await loadObjectiveCourses();
    } catch (error) {
      console.error("Error updating objective course:", error);
      setStatusMessage(
        getApiErrorMessage(error, "Could not update objective course."),
      );
    }
  };

  const handleDeleteCourse = async () => {
    if (!selectedCourseId) return;
    const confirmed = window.confirm(
      "Delete this objective course and all saved entries in it?",
    );
    if (!confirmed) return;

    try {
      await teacherAPI.deleteObjectiveCourse(selectedCourseId);
      setStatusMessage("Objective course deleted.");
      setEntries([]);
      resetEntryDraft("");
      setIsCourseSettingsModalOpen(false);
      await loadObjectiveCourses();
    } catch (error) {
      console.error("Error deleting objective course:", error);
      setStatusMessage(
        getApiErrorMessage(error, "Could not delete objective course."),
      );
    }
  };

  const handleSaveEntry = async (event) => {
    event.preventDefault();
    const objectiveCourseId = selectedCourseId || entryDraft.objectiveCourseId;
    if (!objectiveCourseId) {
      setStatusMessage("Select an objective course first.");
      return;
    }

    const payload = {
      objectiveCourseId,
      unitLabel: entryDraft.unitLabel || null,
      lessonLabel: entryDraft.lessonLabel || null,
      objectiveDate: toDateKey(entryDraft.objectiveDate) || null,
      weekStartDate: toDateKey(entryDraft.weekStartDate) || null,
      weekLabel: entryDraft.weekLabel || null,
      teksCode: String(entryDraft.teksCode || "").trim(),
      teksFullText: entryDraft.teksFullText || null,
      learningObjective: String(entryDraft.learningObjective || "").trim(),
      iCanStatements: toICanArray(entryDraft.iCanText),
      notes: entryDraft.notes || null,
    };

    if (!payload.teksCode || !payload.learningObjective) {
      setStatusMessage("TEKS code and learning objective are required.");
      return;
    }

    try {
      if (entryDraft.id) {
        await teacherAPI.updateObjectiveEntry(entryDraft.id, payload);
        setStatusMessage("Objective entry updated.");
      } else {
        await teacherAPI.createObjectiveEntry(payload);
        setStatusMessage("Objective entry saved.");
      }
      resetEntryDraft(objectiveCourseId);
      setIsEntryModalOpen(false);
      await loadObjectiveEntries(objectiveCourseId, appliedFilters);
      await loadFilterOptions(objectiveCourseId);
    } catch (error) {
      console.error("Error saving objective entry:", error);
      setStatusMessage(
        getApiErrorMessage(error, "Could not save objective entry."),
      );
    }
  };

  const handleEditEntry = (entry) => {
    if (String(selectedCourseId) !== String(entry.objective_course_id)) {
      setSelectedCourseId(entry.objective_course_id);
    }
    setEntryDraft({
      id: entry.id,
      objectiveCourseId: entry.objective_course_id,
      unitLabel: String(entry.unit_label || ""),
      lessonLabel: String(entry.lesson_label || ""),
      objectiveDate: toDateKey(entry.objective_date),
      weekStartDate: toDateKey(entry.week_start_date),
      weekLabel: String(entry.week_label || ""),
      teksCode: String(entry.teks_code || ""),
      teksFullText: String(entry.teks_full_text || ""),
      learningObjective: String(entry.learning_objective || ""),
      iCanText: toICanText(entry.i_can_statements),
      notes: String(entry.notes || ""),
    });
    setIsEntryModalOpen(true);
  };

  const handleDeleteEntry = async (entryId) => {
    if (!entryId) return;
    const confirmed = window.confirm("Delete this saved objective entry?");
    if (!confirmed) return;

    try {
      await teacherAPI.deleteObjectiveEntry(entryId);
      setStatusMessage("Objective entry deleted.");
      if (String(entryDraft.id) === String(entryId)) {
        resetEntryDraft(selectedCourseId);
      }
      await loadObjectiveEntries(selectedCourseId, appliedFilters);
      await loadFilterOptions(selectedCourseId);
    } catch (error) {
      console.error("Error deleting objective entry:", error);
      setStatusMessage(
        getApiErrorMessage(error, "Could not delete objective entry."),
      );
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await teacherAPI.downloadObjectiveImportTemplate();
      const csvText = String(response.data || "");
      const blob = new Blob([csvText], { type: "text/csv;charset=utf-8" });
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = "objective-library-upload-template.csv";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error("Error downloading objective library template:", error);
      setBulkError(
        getApiErrorMessage(error, "Could not download CSV template right now."),
      );
    }
  };

  const handleSelectCsvFile = async (event) => {
    const file = event.target?.files?.[0];
    setBulkValidation(null);
    setBulkError("");

    if (!file) {
      setBulkCsvText("");
      setBulkCsvFileName("");
      return;
    }

    try {
      const csvText = await readFileAsText(file);
      if (!csvText.trim()) {
        setBulkCsvText("");
        setBulkCsvFileName(file.name);
        setBulkError("Selected file is empty.");
        return;
      }
      if (csvText.length > MAX_CSV_IMPORT_CHARS) {
        setBulkCsvText("");
        setBulkCsvFileName(file.name);
        setBulkError(
          `CSV is too large. Maximum size is ${MAX_CSV_IMPORT_CHARS} characters.`,
        );
        return;
      }
      setBulkCsvText(csvText);
      setBulkCsvFileName(file.name);
    } catch (error) {
      console.error("Error reading objective CSV file:", error);
      setBulkCsvText("");
      setBulkCsvFileName(file?.name || "");
      setBulkError("Could not read the selected CSV file.");
    }
  };

  const handleValidateCsv = async () => {
    if (!selectedCourseId) {
      setBulkError("Select a target objective course first.");
      return;
    }
    if (!bulkCsvText.trim()) {
      setBulkError("Choose a CSV file first.");
      return;
    }

    setBulkValidating(true);
    setBulkError("");
    setBulkValidation(null);
    try {
      const response = await teacherAPI.validateObjectiveImportCsv({
        objectiveCourseId: selectedCourseId,
        csvText: bulkCsvText,
      });
      setBulkValidation(response.data || null);
      if ((response.data?.summary?.invalidRows || 0) > 0) {
        setStatusMessage("CSV validated with errors. Fix CSV rows before importing.");
      } else {
        setStatusMessage("CSV validation passed.");
      }
    } catch (error) {
      console.error("Error validating objective CSV:", error);
      setBulkValidation(error?.response?.data || null);
      setBulkError(
        getApiErrorMessage(error, "CSV validation failed."),
      );
    } finally {
      setBulkValidating(false);
    }
  };

  const handleImportCsv = async () => {
    if (!selectedCourseId) {
      setBulkError("Select a target objective course first.");
      return;
    }
    if (!bulkValidation?.summary?.validRows) {
      setBulkError("Validate CSV first.");
      return;
    }
    if ((bulkValidation?.summary?.invalidRows || 0) > 0) {
      setBulkError("Cannot import while validation errors exist.");
      return;
    }

    setBulkImporting(true);
    setBulkError("");
    try {
      const response = await teacherAPI.importObjectiveEntriesCsv({
        objectiveCourseId: selectedCourseId,
        csvText: bulkCsvText,
      });
      const insertedCount = Number(response?.data?.insertedCount || 0);
      setStatusMessage(`Imported ${insertedCount} objective rows.`);
      await loadObjectiveEntries(selectedCourseId, appliedFilters);
      await loadFilterOptions(selectedCourseId);
    } catch (error) {
      console.error("Error importing objective CSV:", error);
      setBulkError(getApiErrorMessage(error, "CSV import failed."));
      if (error?.response?.data?.summary || error?.response?.data?.errors) {
        setBulkValidation(error.response.data);
      }
    } finally {
      setBulkImporting(false);
    }
  };

  const handleCopyPrompt = async (promptText, label) => {
    try {
      await navigator.clipboard.writeText(promptText);
      setAiPromptStatus(`${label} copied.`);
    } catch (_error) {
      setAiPromptStatus(`Could not copy ${label.toLowerCase()}.`);
    }
  };

  const openCourseSettingsModal = (courseId) => {
    setSelectedCourseId(courseId);
    setIsCourseSettingsModalOpen(true);
  };

  const handleBulkTargetCourseChange = (event) => {
    const nextValue = String(event.target.value || "");
    if (nextValue === BULK_CREATE_COURSE_OPTION) {
      setShowCreateCourseForm(true);
      return;
    }
    setSelectedCourseId(nextValue);
  };

  const toggleBulkTools = () => {
    setShowBulkTools((prev) => !prev);
    setShowBulkHelpers(false);
  };

  const toggleBulkHelpers = () => {
    setShowBulkHelpers((prev) => !prev);
  };

  const openWeekModal = () => {
    const preferredObjectiveCourseId = selectedCourseId || objectiveCourses[0]?.id || "";
    const linkedCanonicalForSelectedObjectiveCourse = objectiveCourses.find(
      (course) => String(course.id) === String(preferredObjectiveCourseId),
    );
    const preferredSourceCourseId =
      String(currentCourse || "").trim()
      || String(
        linkedCanonicalForSelectedObjectiveCourse?.canonical_course_id
          || linkedCanonicalForSelectedObjectiveCourse?.canonicalCourseId
          || "",
      ).trim()
      || String(canonicalCourses[0]?.id || "").trim();
    resetWeekDraft(preferredObjectiveCourseId, preferredSourceCourseId);
    setIsWeekModalOpen(true);
    setWeekStatusMessage("");
    setActiveWeekTeksRowKey("");
    setWeekTeksSearchInput("");
    setWeekTeksSearchDebounced("");

    if (!preferredSourceCourseId) {
      setWeekStatusMessage("Select a source district course to load canonical defaults.");
      return;
    }
    if (!preferredObjectiveCourseId) {
      setWeekStatusMessage("You can load defaults now. Choose a target objective course before saving.");
    }
  };

  const handleLoadDefaultWeekObjectives = async () => {
    if (!selectedWeekCanonicalCourseId) {
      setWeekStatusMessage("Select a source district course to load default objectives.");
      return;
    }

    const weekStartDate = toMondayDateKey(weekDraft.weekStartDate || new Date());
    const weekEndDate = addDaysToDateKey(weekStartDate, 4);

    setWeekLoadingDefaults(true);
    setWeekStatusMessage("");
    try {
      const response = await kioskAPI.getObjectivesRange(
        selectedWeekCanonicalCourseId,
        weekStartDate,
        weekEndDate,
      );
      const rows = Array.isArray(response?.data) ? response.data : [];
      const mappedRows = rows
        .map((row) => ({
          dayKey: toWeekDayKey(row.calendar_date),
          teksCodes: String(row.teks_code || "").trim(),
          learningObjective: String(row.objective_statement || "").trim(),
          iCanText: Array.isArray(row.i_can_statements)
            ? row.i_can_statements.map((line) => String(line || "").trim()).filter(Boolean).join("\n")
            : "",
          notes: String(row.notes || "").trim(),
        }))
        .filter(
          (row) =>
            row.learningObjective
            || row.teksCodes
            || row.iCanText
            || row.notes,
        );

      if (!mappedRows.length) {
        setWeekStatusMessage(
          "No district default objectives found for this week. You can still add rows manually.",
        );
        return;
      }

      setWeekDraft((prev) => ({
        ...prev,
        weekStartDate,
        mode: "by_day",
        rows: mappedRows,
      }));
      setWeekStatusMessage(
        `Loaded ${mappedRows.length} default objective row${mappedRows.length === 1 ? "" : "s"} for editing. Review and save.`,
      );
    } catch (error) {
      console.error("Error loading district default objectives:", error);
      setWeekStatusMessage(
        getApiErrorMessage(
          error,
          "Could not load district default objectives right now.",
        ),
      );
    } finally {
      setWeekLoadingDefaults(false);
    }
  };

  const createWeekCourse = async (overrideTitle = "") => {
    const title = String(overrideTitle || weekCourseTitle || "").trim();
    if (!title) {
      setWeekStatusMessage("Enter a course title to create a new course.");
      return;
    }

    setWeekCourseCreating(true);
    try {
      const response = await teacherAPI.createObjectiveCourse({
        title,
        subject: null,
        gradeLevel: null,
        canonicalCourseId: selectedWeekCanonicalCourseId || null,
      });
      const created = response?.data || null;
      if (created?.id) {
        await loadObjectiveCourses();
        setSelectedCourseId(String(created.id));
        setWeekDraft((prev) => ({ ...prev, objectiveCourseId: String(created.id) }));
        setWeekCourseTitle(title);
        setWeekCourseSuggestedTitle(suggestedWeekCourseTitle);
        setWeekStatusMessage("Course created and selected.");
      }
    } catch (error) {
      console.error("Error creating objective course from week modal:", error);
      setWeekStatusMessage(
        getApiErrorMessage(error, "Could not create course from week planner."),
      );
    } finally {
      setWeekCourseCreating(false);
    }
  };

  const handleCreateWeekCourse = async () => {
    await createWeekCourse();
  };

  const handleCreateSuggestedWeekCourse = async () => {
    if (!suggestedWeekCourseTitle) {
      setWeekStatusMessage("Select a source district course to generate a suggested title.");
      return;
    }
    await createWeekCourse(suggestedWeekCourseTitle);
  };

  const handleWeekRowChange = (index, key, value) => {
    setWeekDraft((prev) => ({
      ...prev,
      rows: prev.rows.map((row, rowIndex) =>
        rowIndex === index ? { ...row, [key]: value } : row,
      ),
    }));
  };

  const handleAddWeekRow = () => {
    setWeekDraft((prev) => ({ ...prev, rows: [...prev.rows, emptyWeekRow()] }));
  };

  const handleRemoveWeekRow = (index) => {
    setWeekDraft((prev) => ({
      ...prev,
      rows: prev.rows.filter((_row, rowIndex) => rowIndex !== index),
    }));
  };

  const handleSaveWeekObjectives = async () => {
    const objectiveCourseId = String(weekDraft.objectiveCourseId || "").trim();
    if (!objectiveCourseId) {
      setWeekStatusMessage("Choose a save target objective course before saving.");
      return;
    }

    const weekStartDate = toMondayDateKey(weekDraft.weekStartDate);
    if (!weekStartDate) {
      setWeekStatusMessage("Set a valid week start date.");
      return;
    }

    const usableRows = (weekDraft.rows || []).filter(
      (row) =>
        String(row.learningObjective || "").trim()
        || String(row.teksCodes || "").trim()
        || String(row.iCanText || "").trim()
        || String(row.notes || "").trim(),
    );
    if (!usableRows.length) {
      setWeekStatusMessage("Add at least one objective row.");
      return;
    }

    const resolvedUnitLabel = String(weekDraft.unitLabel || "").trim();
    const resolvedLessonLabel = String(weekDraft.lessonLabel || "").trim();
    const unitLabel = resolvedUnitLabel || null;
    const lessonLabel = resolvedLessonLabel || null;
    const weekLabel = String(weekDraft.weekLabel || "").trim() || null;

    const payloads = [];
    const errors = [];
    const allowedWeekTeksCodes = new Set(
      weekCourseTeksOptions
        .map((option) => normalizeTeksToken(option.code))
        .filter(Boolean),
    );
    usableRows.forEach((row, index) => {
      const rowNumber = index + 1;
      const learningObjective = String(row.learningObjective || "").trim();
      const teksCodes = toTeksCodes(row.teksCodes);
      if (!learningObjective) {
        errors.push(`Row ${rowNumber}: learning objective is required.`);
        return;
      }
      if (!teksCodes.length) {
        errors.push(`Row ${rowNumber}: add at least one TEKS code.`);
        return;
      }
      if (selectedWeekCanonicalCourseId && allowedWeekTeksCodes.size > 0) {
        const invalidCodes = teksCodes.filter(
          (code) => !matchesAllowedTeksToken(code, allowedWeekTeksCodes),
        );
        if (invalidCodes.length > 0) {
          errors.push(
            `Row ${rowNumber}: choose a TEKS from this course list (${invalidCodes.join(", ")} not found).`,
          );
          return;
        }
      }
      let objectiveDate = null;
      if (weekDraft.mode === "by_day") {
        const selectedDay = WEEKDAY_OPTIONS.find((day) => day.key === row.dayKey);
        objectiveDate = addDaysToDateKey(weekStartDate, selectedDay?.offset || 0) || null;
      }

      const iCanStatements = toICanArray(row.iCanText);
      const notes = String(row.notes || "").trim() || null;
      teksCodes.forEach((teksCode) => {
        payloads.push({
          objectiveCourseId,
          unitLabel,
          lessonLabel,
          objectiveDate,
          weekStartDate,
          weekLabel,
          teksCode,
          learningObjective,
          iCanStatements,
          notes,
        });
      });
    });

    if (errors.length) {
      setWeekStatusMessage(errors[0]);
      return;
    }
    if (!payloads.length) {
      setWeekStatusMessage("No valid objectives to save.");
      return;
    }

    setWeekSaving(true);
    setWeekStatusMessage("");
    let insertedCount = 0;
    let failedCount = 0;
    try {
      for (const payload of payloads) {
        try {
          await teacherAPI.createObjectiveEntry(payload);
          insertedCount += 1;
        } catch (error) {
          failedCount += 1;
          console.error("Error saving weekly objective payload:", error, payload);
        }
      }

      setSelectedCourseId(objectiveCourseId);
      await loadObjectiveEntries(objectiveCourseId, appliedFilters);
      await loadFilterOptions(objectiveCourseId);

      if (failedCount > 0) {
        setWeekStatusMessage(
          `Saved ${insertedCount} objective entries, ${failedCount} failed.`,
        );
      } else {
        setWeekStatusMessage(`Saved ${insertedCount} objective entries.`);
        setStatusMessage(`Saved ${insertedCount} objective entries from week planner.`);
        resetWeekDraft(objectiveCourseId, selectedWeekCanonicalCourseId);
      }
    } finally {
      setWeekSaving(false);
    }
  };

  return (
    <div className="panel-card objective-library-page" data-tour="objective-library-root">
      <header className="objective-library-header">
        <h3>Teacher Objective Library</h3>
        <p>
          Build private objective courses and save TEKS, objectives, and I can statements for
          quick reuse.
        </p>
      </header>

      <section
        className="objective-actions-card"
        data-tour="objective-library-create-course"
      >
        <div className="objective-inline-actions objective-actions-toggle-row">
          <h4>Objective Actions</h4>
          <label className="objective-checkbox">
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(event) => setIncludeArchived(event.target.checked)}
            />
            Show archived
          </label>
          <button
            type="button"
            className="primary-btn"
            onClick={() => setShowCreateCourseForm((prev) => !prev)}
          >
            {showCreateCourseForm ? "Hide Add Course" : "Add Course"}
          </button>
          <button
            type="button"
            className="primary-btn"
            onClick={openWeekModal}
          >
            Add Custom Objectives
          </button>
          <button
            type="button"
            className="primary-btn"
            data-tour="objective-library-bulk-toggle"
            onClick={toggleBulkTools}
          >
            {showBulkTools ? "Hide Bulk Upload" : "Bulk Upload"}
          </button>
        </div>

        {showCreateCourseForm ? (
          <div className="objective-action-panel">
            <form className="objective-form-grid" onSubmit={handleCreateCourse}>
              <div className="field-group">
                <label htmlFor="objective-course-title" className="field-label">Course title</label>
                <input
                  id="objective-course-title"
                  value={newCourseDraft.title}
                  onChange={(event) =>
                    setNewCourseDraft((prev) => ({ ...prev, title: event.target.value }))
                  }
                  placeholder="Ex: Journalism Objectives"
                  required
                />
              </div>
              <div className="field-group">
                <label htmlFor="objective-course-subject" className="field-label">Subject (optional)</label>
                <input
                  id="objective-course-subject"
                  value={newCourseDraft.subject}
                  onChange={(event) =>
                    setNewCourseDraft((prev) => ({ ...prev, subject: event.target.value }))
                  }
                  placeholder="Ex: Social Studies"
                />
              </div>
              <div className="field-group">
                <label htmlFor="objective-course-grade" className="field-label">Grade level (optional)</label>
                <input
                  id="objective-course-grade"
                  value={newCourseDraft.gradeLevel}
                  onChange={(event) =>
                    setNewCourseDraft((prev) => ({ ...prev, gradeLevel: event.target.value }))
                  }
                  inputMode="numeric"
                  placeholder="11"
                />
              </div>
              <div className="field-group">
                <label htmlFor="objective-course-canonical" className="field-label">Linked district course (optional)</label>
                <select
                  id="objective-course-canonical"
                  value={newCourseDraft.canonicalCourseId}
                  onChange={(event) =>
                    setNewCourseDraft((prev) => ({
                      ...prev,
                      canonicalCourseId: event.target.value,
                    }))
                  }
                >
                  <option value="">None</option>
                  {canonicalCourses.map((course) => (
                    <option key={course.id} value={course.id}>
                      {course.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="objective-form-actions">
                <button type="submit" className="primary-btn">Create Course</button>
                <button
                  type="button"
                  className="primary-btn"
                  onClick={() => setShowCreateCourseForm(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        ) : null}

        {showBulkTools ? (
          <div className="objective-action-panel">
            <p className="muted">
              Choose the target objective course in this page first, then upload the CSV.
              Every imported row is assigned to that selected course.
            </p>
            <div className="field-group objective-bulk-course-select">
              <label htmlFor="bulk-target-course" className="field-label">
                Target Objective Course
              </label>
              <select
                id="bulk-target-course"
                value={selectedCourseId || ""}
                onChange={handleBulkTargetCourseChange}
              >
                <option value={BULK_CREATE_COURSE_OPTION}>+ Add Course...</option>
                <option value="">Select a private objective course...</option>
                {objectiveCourses.map((course) => (
                  <option key={course.id} value={course.id}>
                    {course.title}
                    {course.is_archived ? " (Archived)" : ""}
                  </option>
                ))}
              </select>
              {!objectiveCourses.length ? (
                <p className="muted">Create your first objective course to enable bulk upload.</p>
              ) : null}
            </div>
            <ul>
              <li>Do not include a course or course_id column in the CSV.</li>
              <li>Required columns: teks_code, learning_objective.</li>
              <li>Use YYYY-MM-DD format for objective_date.</li>
              <li>If provided, week_start_date must be a Monday (YYYY-MM-DD).</li>
              <li>Use | to separate multiple i_can_statements in one cell.</li>
            </ul>
            <p className="muted">
              Template headers: <code>{CSV_TEMPLATE_HEADERS.join(",")}</code>
            </p>
            <div className="objective-inline-actions objective-bulk-help-toggle-row">
              <button type="button" className="primary-btn" onClick={toggleBulkHelpers}>
                {showBulkHelpers
                  ? "Hide CSV / AI Help"
                  : "Need help saving as CSV? Want to use AI to help build your objective CSV?"}
              </button>
            </div>
            {showBulkHelpers ? (
              <>
                <div className="objective-save-csv-help">
                  <h5>How To Save As CSV</h5>
                  <ol>
                    <li>Build your rows in the template and keep the header row exactly as provided.</li>
                    <li>
                      In Excel: <strong>File</strong> &rarr; <strong>Save As</strong> &rarr; choose
                      <strong> CSV (Comma delimited) (*.csv)</strong>.
                    </li>
                    <li>
                      In Google Sheets: <strong>File</strong> &rarr; <strong>Download</strong> &rarr;
                      <strong> Comma Separated Values (.csv)</strong>.
                    </li>
                    <li>Upload that `.csv` file here and click <strong>Validate CSV</strong> first.</li>
                  </ol>
                  <p className="muted">
                    Tip: if your file says `.xlsx` or `.numbers`, it is not a CSV yet.
                  </p>
                </div>
                <div className="objective-ai-prompts-card">
                  <h5>AI CSV Prompt Helper</h5>
                  <p className="muted">
                    Use one of these prompts with ChatGPT or another AI tool to generate
                    CSV rows that match validation rules.
                  </p>
                  <div className="objective-ai-prompt-item">
                    <div className="objective-inline-actions">
                      <strong>Prompt 1: Create Rows From Scratch</strong>
                      <button
                        type="button"
                        className="primary-btn"
                        onClick={() => handleCopyPrompt(aiPromptFromScratch, "Prompt 1")}
                      >
                        Copy Prompt 1
                      </button>
                    </div>
                    <textarea
                      readOnly
                      rows={9}
                      value={aiPromptFromScratch}
                      aria-label="AI prompt to create CSV rows from scratch"
                    />
                  </div>
                  <div className="objective-ai-prompt-item">
                    <div className="objective-inline-actions">
                      <strong>Prompt 2: Convert Existing Notes</strong>
                      <button
                        type="button"
                        className="primary-btn"
                        onClick={() => handleCopyPrompt(aiPromptNormalizeText, "Prompt 2")}
                      >
                        Copy Prompt 2
                      </button>
                    </div>
                    <textarea
                      readOnly
                      rows={8}
                      value={aiPromptNormalizeText}
                      aria-label="AI prompt to convert notes into CSV rows"
                    />
                  </div>
                  {aiPromptStatus ? <p className="muted">{aiPromptStatus}</p> : null}
                </div>
              </>
            ) : null}
            <div className="objective-inline-actions">
              <button
                type="button"
                className="primary-btn"
                onClick={handleDownloadTemplate}
              >
                Download CSV Template
              </button>
              <label className="objective-file-label" htmlFor="objective-csv-file">
                Step 1 - Upload CSV
              </label>
              <input
                id="objective-csv-file"
                type="file"
                accept=".csv,text/csv"
                onChange={handleSelectCsvFile}
              />
              <button
                type="button"
                className="objective-step-btn"
                onClick={handleValidateCsv}
                disabled={bulkValidating || bulkImporting || !bulkCsvText}
              >
                {bulkValidating ? "Step 2 - Validating..." : "Step 2 - Validate CSV"}
              </button>
              {!bulkCsvText ? (
                <span className="objective-step-hint">Upload CSV before validating.</span>
              ) : null}
              <button
                type="button"
                className="objective-step-btn"
                onClick={handleImportCsv}
                disabled={
                  bulkValidating
                  || bulkImporting
                  || !bulkValidation?.summary
                  || Number(bulkValidation?.summary?.validRows || 0) <= 0
                  || Number(bulkValidation?.summary?.invalidRows || 0) > 0
                }
              >
                {bulkImporting ? "Step 3 - Importing..." : "Step 3 - Import CSV"}
              </button>
            </div>
            {bulkCsvFileName ? (
              <p className="muted">Selected file: {bulkCsvFileName}</p>
            ) : null}
            {bulkError ? <p className="objective-error">{bulkError}</p> : null}
            {bulkValidation?.summary ? (
              <div className="objective-bulk-results">
                <p className="muted">
                  Validation summary: {bulkValidation.summary.validRows} valid,{" "}
                  {bulkValidation.summary.invalidRows} invalid,{" "}
                  {bulkValidation.summary.emptyRowsSkipped || 0} empty rows skipped,{" "}
                  {bulkValidation.summary.totalRows} total data rows.
                </p>
                {Array.isArray(bulkValidation.errors) && bulkValidation.errors.length > 0 ? (
                  <div>
                    <h5>Validation Errors</h5>
                    <ul>
                      {bulkValidation.errors.slice(0, 25).map((error, index) => (
                        <li key={`bulk-error-${index}`}>
                          Row {error.rowNumber} ({error.column}): {error.message}
                        </li>
                      ))}
                    </ul>
                    {bulkValidation.errorLimitReached ? (
                      <p className="muted">
                        Additional errors were truncated. Fix current errors and validate again.
                      </p>
                    ) : null}
                  </div>
                ) : null}
                {Array.isArray(bulkValidation.previewRows) && bulkValidation.previewRows.length > 0 ? (
                  <div className="objective-bulk-preview">
                    <h5>Preview (first {bulkValidation.previewRows.length} rows)</h5>
                    <div className="objective-preview-table-wrap">
                      <table>
                        <thead>
                          <tr>
                            <th>TEKS</th>
                            <th>Learning Objective</th>
                            <th>I can statements</th>
                            <th>Unit</th>
                            <th>Lesson</th>
                            <th>Date</th>
                            <th>Week Start</th>
                            <th>Week Label</th>
                          </tr>
                        </thead>
                        <tbody>
                          {bulkValidation.previewRows.map((row, index) => (
                            <tr key={`bulk-preview-${index}`}>
                              <td>{row.teksCode}</td>
                              <td>{row.learningObjective}</td>
                              <td>{Array.isArray(row.iCanStatements) ? row.iCanStatements.join(" | ") : ""}</td>
                              <td>{row.unitLabel || ""}</td>
                              <td>{row.lessonLabel || ""}</td>
                              <td>{row.objectiveDate || ""}</td>
                              <td>{row.weekStartDate || ""}</td>
                              <td>{row.weekLabel || ""}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}
      </section>

      {statusMessage ? <p className="objective-status">{statusMessage}</p> : null}
      {coursesError ? <p className="objective-error">{coursesError}</p> : null}

      <div className="objective-library-layout">
        <aside
          className="objective-course-list-card"
          data-tour="objective-library-course-list"
        >
          <h4>My Objective Courses</h4>
          {coursesLoading ? <p className="muted">Loading objective courses...</p> : null}
          {!coursesLoading && objectiveCourses.length === 0 ? (
            <p className="muted">No objective courses yet. Create one above.</p>
          ) : null}
          <div className="objective-course-list">
            {objectiveCourses.map((course) => {
              const linkedCanonical = canonicalCourseMap[String(course.canonical_course_id)];
              return (
                <div
                  key={course.id}
                  className={`objective-course-btn ${
                    String(course.id) === String(selectedCourseId) ? "active" : ""
                  }`}
                >
                  <button
                    type="button"
                    className="objective-course-select-btn"
                    onClick={() => setSelectedCourseId(course.id)}
                  >
                    <strong>{course.title}</strong>
                    <span>
                      {course.subject || "No subject"}{" "}
                      {course.grade_level ? `• Grade ${course.grade_level}` : ""}
                    </span>
                    {linkedCanonical ? <small>Linked: {linkedCanonical.name}</small> : null}
                    {course.is_archived ? <small>Archived</small> : null}
                  </button>
                  <button
                    type="button"
                    className="objective-course-edit-btn"
                    aria-label={`Edit ${course.title}`}
                    title={`Edit ${course.title}`}
                    data-tour="objective-library-course-edit"
                    onClick={() => openCourseSettingsModal(course.id)}
                  >
                    <svg
                      viewBox="0 0 24 24"
                      width="16"
                      height="16"
                      aria-hidden="true"
                      focusable="false"
                    >
                      <path
                        d="M4 20h4l10-10-4-4L4 16v4zM14 6l4 4"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              );
            })}
          </div>
        </aside>

        <section className="objective-workspace">
          {!selectedCourse ? (
            <div className="objective-empty">
              <p>Select an objective course to manage entries.</p>
            </div>
          ) : (
            <>
              <div className="objective-entry-filters-card">
                <h4>Find Saved Entries</h4>
                <div className="objective-form-grid">
                  <div className="field-group">
                    <label htmlFor="filter-unit" className="field-label">Unit</label>
                    <select
                      id="filter-unit"
                      value={filterDraft.unitLabel}
                      onChange={(event) =>
                        setFilterDraft((prev) => ({
                          ...prev,
                          unitLabel: event.target.value,
                          lessonLabel: "",
                        }))
                      }
                    >
                      <option value="">All units</option>
                      {unitOptions.map((unitLabel) => (
                        <option key={unitLabel} value={unitLabel}>
                          {unitLabel}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="field-group">
                    <label htmlFor="filter-lesson" className="field-label">Lesson</label>
                    <select
                      id="filter-lesson"
                      value={filterDraft.lessonLabel}
                      onChange={(event) =>
                        setFilterDraft((prev) => ({ ...prev, lessonLabel: event.target.value }))
                      }
                      disabled={!lessonOptions.length}
                    >
                      <option value="">All lessons</option>
                      {lessonOptions.map((lessonLabel) => (
                        <option key={lessonLabel} value={lessonLabel}>
                          {lessonLabel}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="field-group">
                    <label htmlFor="filter-week-start" className="field-label">Week</label>
                    <select
                      id="filter-week-start"
                      value={filterDraft.weekStartDate}
                      onChange={(event) =>
                        setFilterDraft((prev) => ({
                          ...prev,
                          weekStartDate: event.target.value,
                          weekLabel: "",
                        }))
                      }
                    >
                      <option value="">All weeks</option>
                      {weekOptions.map((week) => (
                        <option key={week.value} value={week.value}>
                          {week.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="field-group">
                    <label htmlFor="filter-week-label" className="field-label">Weekly objective</label>
                    <select
                      id="filter-week-label"
                      value={filterDraft.weekLabel}
                      onChange={(event) =>
                        setFilterDraft((prev) => ({ ...prev, weekLabel: event.target.value }))
                      }
                      disabled={!weekLabelOptions.length}
                    >
                      <option value="">All weekly objectives</option>
                      {weekLabelOptions.map((weekLabel) => (
                        <option key={weekLabel} value={weekLabel}>
                          {weekLabel}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="field-group">
                    <label htmlFor="filter-date-from" className="field-label">Date from</label>
                    <input
                      id="filter-date-from"
                      type="date"
                      value={filterDraft.dateFrom}
                      onChange={(event) =>
                        setFilterDraft((prev) => ({ ...prev, dateFrom: event.target.value }))
                      }
                    />
                  </div>
                  <div className="field-group">
                    <label htmlFor="filter-date-to" className="field-label">Date to</label>
                    <input
                      id="filter-date-to"
                      type="date"
                      value={filterDraft.dateTo}
                      onChange={(event) =>
                        setFilterDraft((prev) => ({ ...prev, dateTo: event.target.value }))
                      }
                    />
                  </div>
                </div>
                <div className="objective-inline-actions">
                  <button
                    type="button"
                    className="primary-btn"
                    onClick={() => setAppliedFilters({ ...filterDraft })}
                  >
                    Apply Filters
                  </button>
                  <button
                    type="button"
                    className="primary-btn"
                    onClick={() => {
                      const cleared = {
                        unitLabel: "",
                        lessonLabel: "",
                        weekStartDate: "",
                        weekLabel: "",
                        dateFrom: "",
                        dateTo: "",
                      };
                      setFilterDraft(cleared);
                      setAppliedFilters(cleared);
                    }}
                  >
                    Clear Filters
                  </button>
                </div>
                {entriesError ? <p className="objective-error">{entriesError}</p> : null}
              </div>

              <div
                className="objective-entry-list-card"
                data-tour="objective-library-saved-entries"
              >
                <h4>Saved Entries</h4>
                {entriesLoading ? <p className="muted">Loading entries...</p> : null}
                {!entriesLoading && entries.length === 0 ? (
                  <p className="muted">No entries match your filters yet.</p>
                ) : null}
                <div className="objective-entry-list">
                  {entries.map((entry) => (
                    <article key={entry.id} className="objective-entry-item">
                      <header>
                        <strong>{entry.teks_code}</strong>
                        <div className="objective-entry-actions">
                          <button
                            type="button"
                            className="primary-btn"
                            onClick={() => handleEditEntry(entry)}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="primary-btn objective-danger-btn"
                            onClick={() => handleDeleteEntry(entry.id)}
                          >
                            Delete
                          </button>
                        </div>
                      </header>
                      <p>{entry.learning_objective}</p>
                      {(entry.unit_label
                        || entry.lesson_label
                        || entry.objective_date
                        || entry.week_start_date
                        || entry.week_label) && (
                        <p className="muted">
                          {entry.unit_label ? `Unit: ${entry.unit_label}` : ""}
                          {entry.unit_label && entry.lesson_label ? " • " : ""}
                          {entry.lesson_label ? `Lesson: ${entry.lesson_label}` : ""}
                          {(entry.unit_label || entry.lesson_label) && entry.objective_date ? " • " : ""}
                          {entry.objective_date ? `Date: ${toDateKey(entry.objective_date)}` : ""}
                          {(entry.unit_label || entry.lesson_label || entry.objective_date)
                            && entry.week_start_date ? " • " : ""}
                          {entry.week_start_date ? `Week of: ${toDateKey(entry.week_start_date)}` : ""}
                          {(entry.unit_label
                            || entry.lesson_label
                            || entry.objective_date
                            || entry.week_start_date)
                            && entry.week_label ? " • " : ""}
                          {entry.week_label ? `Week label: ${entry.week_label}` : ""}
                        </p>
                      )}
                      {entry.teks_full_text ? (
                        <p className="objective-entry-full-teks">{entry.teks_full_text}</p>
                      ) : null}
                      {Array.isArray(entry.i_can_statements) && entry.i_can_statements.length > 0 ? (
                        <ul>
                          {entry.i_can_statements.map((line, idx) => (
                            <li key={`${entry.id}-${idx}`}>{line}</li>
                          ))}
                        </ul>
                      ) : null}
                      {entry.notes ? <p className="muted">Notes: {entry.notes}</p> : null}
                    </article>
                  ))}
                </div>
              </div>
            </>
          )}
        </section>
      </div>

      {isWeekModalOpen ? (
        <div
          className="objective-modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-label="Add custom objectives"
          onClick={() => setIsWeekModalOpen(false)}
        >
          <div
            className="objective-modal-card objective-week-modal-card"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="objective-modal-header">
              <h4>Add Custom Objectives</h4>
              <button
                type="button"
                className="primary-btn"
                onClick={() => setIsWeekModalOpen(false)}
              >
                Close
              </button>
            </div>

            <div className="objective-form-grid">
              <div className="field-group">
                <label htmlFor="week-source-course-select" className="field-label">
                  Source district course
                </label>
                <select
                  id="week-source-course-select"
                  value={weekDraft.sourceCourseId}
                  onChange={(event) =>
                    setWeekDraft((prev) => ({ ...prev, sourceCourseId: event.target.value }))
                  }
                >
                  <option value="">Select district course for defaults...</option>
                  {canonicalCourses.map((course) => (
                    <option key={course.id} value={course.id}>
                      {course.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field-group">
                <label htmlFor="week-course-select" className="field-label">
                  Save target (private objective course)
                </label>
                <select
                  id="week-course-select"
                  value={weekDraft.objectiveCourseId}
                  onChange={(event) =>
                    setWeekDraft((prev) => ({ ...prev, objectiveCourseId: event.target.value }))
                  }
                >
                  <option value="">Choose before saving...</option>
                  {objectiveCourses.map((course) => (
                    <option key={course.id} value={course.id}>
                      {course.title}
                      {course.is_archived ? " (Archived)" : ""}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field-group">
                <label htmlFor="week-course-title" className="field-label">Or add course</label>
                <div className="objective-inline-actions objective-week-inline">
                  <input
                    id="week-course-title"
                    value={weekCourseTitle}
                    onChange={(event) => setWeekCourseTitle(event.target.value)}
                    placeholder="Ex: Week-at-a-Glance Objectives"
                  />
                  <button
                    type="button"
                    className="primary-btn"
                    onClick={handleCreateWeekCourse}
                    disabled={weekCourseCreating}
                  >
                    {weekCourseCreating ? "Adding..." : "Add Course"}
                  </button>
                  <button
                    type="button"
                    className="primary-btn"
                    onClick={handleCreateSuggestedWeekCourse}
                    disabled={weekCourseCreating || !suggestedWeekCourseTitle}
                  >
                    {weekCourseCreating ? "Adding..." : "Create Suggested Course"}
                  </button>
                </div>
                {!weekDraft.objectiveCourseId && suggestedWeekCourseTitle ? (
                  <p className="muted">
                    Suggested: <strong>{suggestedWeekCourseTitle}</strong>
                  </p>
                ) : null}
              </div>
              <div className="field-group">
                <label htmlFor="week-start-date" className="field-label">Week start (Monday)</label>
                <input
                  id="week-start-date"
                  type="date"
                  value={weekDraft.weekStartDate}
                  onChange={(event) =>
                    setWeekDraft((prev) => ({
                      ...prev,
                      weekStartDate: toMondayDateKey(event.target.value),
                    }))
                  }
                />
              </div>
              <div className="field-group">
                <label htmlFor="week-label" className="field-label">Week label (optional)</label>
                <input
                  id="week-label"
                  value={weekDraft.weekLabel}
                  onChange={(event) =>
                    setWeekDraft((prev) => ({ ...prev, weekLabel: event.target.value }))
                  }
                  placeholder="Week 4"
                />
              </div>
              <div className="field-group">
                <label htmlFor="week-unit-label" className="field-label">Unit (optional)</label>
                <input
                  id="week-unit-label"
                  value={weekDraft.unitLabel}
                  onChange={(event) =>
                    setWeekDraft((prev) => ({ ...prev, unitLabel: event.target.value }))
                  }
                  placeholder="Unit 3"
                />
              </div>
              <div className="field-group">
                <label htmlFor="week-lesson-label" className="field-label">Lesson (optional)</label>
                <input
                  id="week-lesson-label"
                  value={weekDraft.lessonLabel}
                  onChange={(event) =>
                    setWeekDraft((prev) => ({ ...prev, lessonLabel: event.target.value }))
                  }
                  placeholder="Lesson 2"
                />
              </div>
            </div>

            <div className="objective-inline-actions">
              <button
                type="button"
                className="primary-btn"
                onClick={handleLoadDefaultWeekObjectives}
                disabled={weekLoadingDefaults}
              >
                {weekLoadingDefaults ? "Loading Defaults..." : "Load Default for Edit"}
              </button>
            </div>

            <div className="objective-inline-actions">
              <label className="objective-checkbox objective-week-toggle">
                <input
                  type="radio"
                  name="week-mode"
                  checked={weekDraft.mode === "by_day"}
                  onChange={() => setWeekDraft((prev) => ({ ...prev, mode: "by_day" }))}
                />
                Plan by day (Mon-Fri)
              </label>
              <label className="objective-checkbox objective-week-toggle">
                <input
                  type="radio"
                  name="week-mode"
                  checked={weekDraft.mode === "whole_week"}
                  onChange={() => setWeekDraft((prev) => ({ ...prev, mode: "whole_week" }))}
                />
                <span
                  className="objective-hint-label"
                  title="Add a set of weekly objectives to easily publish to your kiosk for the week."
                  aria-label="Add a set of weekly objectives to easily publish to your kiosk for the week."
                >
                  Whole week objective (no day date)
                </span>
              </label>
            </div>

            <div className="objective-week-rows">
              {weekDraft.rows.map((row, index) => (
                <div key={`week-row-${index}`} className="objective-week-row">
                  <div className="objective-week-row-header">
                    <strong>Objective Row {index + 1}</strong>
                    <button
                      type="button"
                      className="primary-btn objective-danger-btn"
                      onClick={() => handleRemoveWeekRow(index)}
                      disabled={weekDraft.rows.length <= 1}
                    >
                      Remove
                    </button>
                  </div>
                  <div className="objective-form-grid">
                    {weekDraft.mode === "by_day" ? (
                      <div className="field-group">
                        <label htmlFor={`week-day-${index}`} className="field-label">Day</label>
                        <select
                          id={`week-day-${index}`}
                          value={row.dayKey}
                          onChange={(event) =>
                            handleWeekRowChange(index, "dayKey", event.target.value)
                          }
                        >
                          {WEEKDAY_OPTIONS.map((day) => (
                            <option key={day.key} value={day.key}>
                              {day.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    ) : null}
                    <div className="field-group">
                      <label htmlFor={`week-teks-${index}`} className="field-label">
                        TEKS code
                      </label>
                      {(() => {
                        const pickerKey = `week-row-${index}`;
                        const isPickerActive = activeWeekTeksRowKey === pickerKey;
                        const inputValue = isPickerActive
                          ? weekTeksSearchInput
                          : String(row.teksCodes || "");
                        const hasOptions =
                          !weekCourseTeksLoading && filteredWeekCourseTeksOptions.length > 0;

                        return (
                          <div className="objective-week-teks-picker">
                            <input
                              id={`week-teks-${index}`}
                              type="search"
                              autoComplete="off"
                              value={inputValue}
                              placeholder={weekCourseTeksLoading ? "Loading TEKS..." : "Search course TEKS"}
                              onFocus={(event) => {
                                setActiveWeekTeksRowKey(pickerKey);
                                setWeekTeksSearchInput(String(row.teksCodes || ""));
                                window.requestAnimationFrame(() => {
                                  event.target.select();
                                });
                              }}
                              onChange={(event) => {
                                const nextValue = String(event.target.value || "");
                                setActiveWeekTeksRowKey(pickerKey);
                                setWeekTeksSearchInput(nextValue);
                                const exactMatch = weekCourseTeksOptions.find(
                                  (option) =>
                                    String(option.code || "").toLowerCase()
                                    === nextValue.trim().toLowerCase(),
                                );
                                if (!exactMatch) return;
                                handleWeekRowChange(index, "teksCodes", exactMatch.code);
                              }}
                              onKeyDown={(event) => {
                                if (event.key !== "Enter") return;
                                if (!hasOptions) return;
                                event.preventDefault();
                                const firstMatch = filteredWeekCourseTeksOptions[0];
                                handleWeekRowChange(index, "teksCodes", firstMatch.code);
                                setWeekTeksSearchInput(firstMatch.code);
                                setActiveWeekTeksRowKey("");
                              }}
                              onBlur={() => {
                                window.setTimeout(() => {
                                  if (activeWeekTeksRowKeyRef.current !== pickerKey) return;
                                  setActiveWeekTeksRowKey("");
                                  setWeekTeksSearchInput("");
                                }, 120);
                              }}
                            />
                            {isPickerActive && (
                              <div className="objective-week-teks-menu" role="listbox">
                                {weekCourseTeksLoading && (
                                  <div className="objective-week-teks-empty">Loading TEKS...</div>
                                )}
                                {!weekCourseTeksLoading && hasOptions && (
                                  filteredWeekCourseTeksOptions.map((option) => (
                                    <button
                                      key={option.code}
                                      type="button"
                                      className="objective-week-teks-option"
                                      onMouseDown={(event) => {
                                        event.preventDefault();
                                        handleWeekRowChange(index, "teksCodes", option.code);
                                        setWeekTeksSearchInput(option.code);
                                        setActiveWeekTeksRowKey("");
                                      }}
                                    >
                                      <strong>{option.code}</strong>
                                      {option.description ? <span>{option.description}</span> : null}
                                    </button>
                                  ))
                                )}
                                {!weekCourseTeksLoading && !hasOptions && (
                                  <div className="objective-week-teks-empty">
                                    {weekCourseTeksError || "No TEKS match for that search."}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                  <div className="field-group">
                    <label htmlFor={`week-objective-${index}`} className="field-label">
                      Learning objective
                    </label>
                    <textarea
                      id={`week-objective-${index}`}
                      value={row.learningObjective}
                      onChange={(event) =>
                        handleWeekRowChange(index, "learningObjective", event.target.value)
                      }
                      rows={2}
                      placeholder="The student will..."
                    />
                  </div>
                  <div className="field-group">
                    <label htmlFor={`week-ican-${index}`} className="field-label">
                      I can statements (one per line)
                    </label>
                    <textarea
                      id={`week-ican-${index}`}
                      value={row.iCanText}
                      onChange={(event) =>
                        handleWeekRowChange(index, "iCanText", event.target.value)
                      }
                      rows={3}
                      placeholder={"I can identify...\nI can explain..."}
                    />
                  </div>
                  <div className="field-group">
                    <label htmlFor={`week-notes-${index}`} className="field-label">Notes (optional)</label>
                    <textarea
                      id={`week-notes-${index}`}
                      value={row.notes}
                      onChange={(event) =>
                        handleWeekRowChange(index, "notes", event.target.value)
                      }
                      rows={2}
                    />
                  </div>
                </div>
              ))}
            </div>
            {weekCourseTeksError ? <p className="muted">{weekCourseTeksError}</p> : null}
            {!weekCourseTeksLoading
            && !weekCourseTeksError
            && selectedWeekCanonicalCourseId
            && weekCourseTeksOptions.length === 0 ? (
              <p className="muted">
                No TEKS found for the selected source district course. Add TEKS in Curriculum Hub first.
              </p>
              ) : null}

            <div className="objective-inline-actions">
              <button type="button" className="primary-btn" onClick={handleAddWeekRow}>
                Add Objective Row
              </button>
              <button
                type="button"
                className="primary-btn"
                onClick={handleSaveWeekObjectives}
                disabled={weekSaving}
              >
                {weekSaving ? "Saving..." : "Save Week Objectives"}
              </button>
              <button
                type="button"
                className="primary-btn"
                onClick={() =>
                  resetWeekDraft(weekDraft.objectiveCourseId, weekDraft.sourceCourseId)
                }
                disabled={weekSaving}
              >
                Reset
              </button>
            </div>
            {weekStatusMessage ? <p className="muted">{weekStatusMessage}</p> : null}
          </div>
        </div>
      ) : null}

      {isCourseSettingsModalOpen && selectedCourse ? (
        <div
          className="objective-modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-label="Course settings"
          onClick={() => setIsCourseSettingsModalOpen(false)}
        >
          <div
            className="objective-modal-card"
            data-tour="objective-library-course-settings"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="objective-modal-header">
              <h4>Edit Course Settings</h4>
              <button
                type="button"
                className="primary-btn"
                onClick={() => setIsCourseSettingsModalOpen(false)}
              >
                Close
              </button>
            </div>
            <div className="objective-form-grid">
              <div className="field-group">
                <label htmlFor="selected-course-title" className="field-label">Title</label>
                <input
                  id="selected-course-title"
                  value={selectedCourseDraft.title}
                  onChange={(event) =>
                    setSelectedCourseDraft((prev) => ({ ...prev, title: event.target.value }))
                  }
                />
              </div>
              <div className="field-group">
                <label htmlFor="selected-course-subject" className="field-label">Subject</label>
                <input
                  id="selected-course-subject"
                  value={selectedCourseDraft.subject}
                  onChange={(event) =>
                    setSelectedCourseDraft((prev) => ({ ...prev, subject: event.target.value }))
                  }
                />
              </div>
              <div className="field-group">
                <label htmlFor="selected-course-grade" className="field-label">Grade level</label>
                <input
                  id="selected-course-grade"
                  value={selectedCourseDraft.gradeLevel}
                  onChange={(event) =>
                    setSelectedCourseDraft((prev) => ({
                      ...prev,
                      gradeLevel: event.target.value,
                    }))
                  }
                  inputMode="numeric"
                />
              </div>
              <div className="field-group">
                <label htmlFor="selected-course-canonical" className="field-label">Linked district course</label>
                <select
                  id="selected-course-canonical"
                  value={selectedCourseDraft.canonicalCourseId}
                  onChange={(event) =>
                    setSelectedCourseDraft((prev) => ({
                      ...prev,
                      canonicalCourseId: event.target.value,
                    }))
                  }
                >
                  <option value="">None</option>
                  {canonicalCourses.map((course) => (
                    <option key={course.id} value={course.id}>
                      {course.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="objective-inline-actions">
              <label className="objective-checkbox">
                <input
                  type="checkbox"
                  checked={selectedCourseDraft.isArchived}
                  onChange={(event) =>
                    setSelectedCourseDraft((prev) => ({
                      ...prev,
                      isArchived: event.target.checked,
                    }))
                  }
                />
                Archived
              </label>
              <button type="button" className="primary-btn" onClick={handleSaveCourseDetails}>
                Save Course
              </button>
              <button
                type="button"
                className="primary-btn objective-danger-btn"
                onClick={handleDeleteCourse}
              >
                Delete Course
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {isEntryModalOpen && selectedCourse ? (
        <div
          className="objective-modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-label="Objective entry editor"
          onClick={() => setIsEntryModalOpen(false)}
        >
          <div
            className="objective-modal-card"
            data-tour="objective-library-entry-editor"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="objective-modal-header">
              <h4>{entryDraft.id ? "Edit Objective Entry" : "Add Objective Entry"}</h4>
              <button
                type="button"
                className="primary-btn"
                onClick={() => setIsEntryModalOpen(false)}
              >
                Close
              </button>
            </div>
            <form className="objective-entry-form" onSubmit={handleSaveEntry}>
              <div className="objective-form-grid">
                <div className="field-group">
                  <label htmlFor="entry-unit-label" className="field-label">Unit</label>
                  <input
                    id="entry-unit-label"
                    value={entryDraft.unitLabel}
                    onChange={(event) =>
                      setEntryDraft((prev) => ({ ...prev, unitLabel: event.target.value }))
                    }
                    placeholder="Unit 4"
                  />
                </div>
                <div className="field-group">
                  <label htmlFor="entry-lesson-label" className="field-label">Lesson</label>
                  <input
                    id="entry-lesson-label"
                    value={entryDraft.lessonLabel}
                    onChange={(event) =>
                      setEntryDraft((prev) => ({ ...prev, lessonLabel: event.target.value }))
                    }
                    placeholder="Lesson 2"
                  />
                </div>
                <div className="field-group">
                  <label htmlFor="entry-objective-date" className="field-label">Date</label>
                  <input
                    id="entry-objective-date"
                    type="date"
                    value={entryDraft.objectiveDate}
                    onChange={(event) =>
                      setEntryDraft((prev) => ({
                        ...prev,
                        objectiveDate: event.target.value,
                        weekStartDate: prev.weekStartDate || toMondayDateKey(event.target.value),
                      }))
                    }
                  />
                </div>
                <div className="field-group">
                  <label htmlFor="entry-week-start-date" className="field-label">Week start (Monday)</label>
                  <input
                    id="entry-week-start-date"
                    type="date"
                    value={entryDraft.weekStartDate}
                    onChange={(event) =>
                      setEntryDraft((prev) => ({ ...prev, weekStartDate: event.target.value }))
                    }
                  />
                </div>
                <div className="field-group">
                  <label htmlFor="entry-teks-code" className="field-label">TEKS code</label>
                  <input
                    id="entry-teks-code"
                    value={entryDraft.teksCode}
                    onChange={(event) =>
                      setEntryDraft((prev) => ({ ...prev, teksCode: event.target.value }))
                    }
                    placeholder="USH.16B"
                    required
                  />
                </div>
              </div>
              <div className="field-group">
                <label htmlFor="entry-week-label" className="field-label">Week label (optional)</label>
                <input
                  id="entry-week-label"
                  value={entryDraft.weekLabel}
                  onChange={(event) =>
                    setEntryDraft((prev) => ({ ...prev, weekLabel: event.target.value }))
                  }
                  placeholder="Week 4"
                />
              </div>
              <div className="field-group">
                <label htmlFor="entry-learning-objective" className="field-label">
                  Learning objective
                </label>
                <textarea
                  id="entry-learning-objective"
                  value={entryDraft.learningObjective}
                  onChange={(event) =>
                    setEntryDraft((prev) => ({
                      ...prev,
                      learningObjective: event.target.value,
                    }))
                  }
                  rows={3}
                  placeholder="The student will..."
                  required
                />
              </div>
              <div className="field-group">
                <label htmlFor="entry-i-can" className="field-label">I can statements (one per line)</label>
                <textarea
                  id="entry-i-can"
                  value={entryDraft.iCanText}
                  onChange={(event) =>
                    setEntryDraft((prev) => ({ ...prev, iCanText: event.target.value }))
                  }
                  rows={4}
                  placeholder={"I can identify...\nI can explain..."}
                />
              </div>
              <div className="field-group">
                <label htmlFor="entry-teks-full-text" className="field-label">Full TEKS text (optional)</label>
                <textarea
                  id="entry-teks-full-text"
                  value={entryDraft.teksFullText}
                  onChange={(event) =>
                    setEntryDraft((prev) => ({ ...prev, teksFullText: event.target.value }))
                  }
                  rows={3}
                  placeholder="Paste full TEKS wording if needed."
                />
              </div>
              <div className="field-group">
                <label htmlFor="entry-notes" className="field-label">Notes (optional)</label>
                <textarea
                  id="entry-notes"
                  value={entryDraft.notes}
                  onChange={(event) =>
                    setEntryDraft((prev) => ({ ...prev, notes: event.target.value }))
                  }
                  rows={2}
                />
              </div>
              <div className="objective-inline-actions">
                <button type="submit" className="primary-btn">
                  {entryDraft.id ? "Update Entry" : "Save Entry"}
                </button>
                <button
                  type="button"
                  className="primary-btn"
                  onClick={() => resetEntryDraft(selectedCourseId)}
                >
                  Clear Form
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ObjectiveLibraryPage;
